import type { RoomView, SettingsPane } from '../nav';

/** A room-web screen: every section but Settings, which is a frame, and every Settings pane. */
export type RoomScreenName = Exclude<RoomView, 'settings'> | SettingsPane;

/**
 * room-web's screens and the engagement code each reports on entry (ADR 0012 step 18).
 *
 * <p>One table rather than a literal at each call site, for the portal's reason: a code the server does
 * not know refuses the whole batch it rides in and the client never retries, so a typo here is silence.
 * `screens.test.js` holds each code to the server's checked-in list, and `ActivityCatalogTests` reads
 * this file and checks each is an engagement type.</p>
 *
 * <p>`console.room_<area>`, the mirror of admin-web's `console.admin_<area>`: one staff console family,
 * with the app in the name because several areas exist in both consoles and are different screens. The
 * Room pane is `console.room_room_viewed` — literal, and not the `console.room_settings_viewed` an
 * earlier name used for it on dev.</p>
 *
 * <p>Each code is filed server-side under the feature the screen is about — Tournaments counts toward
 * Tournaments — with the room's Overview and the operator's own Preferences under Consoles. Settings is
 * not a screen of its own: its panes are, and each reports itself.</p>
 */
export const ROOM_SCREENS: Record<RoomScreenName, string> = {
  overview: 'console.room_overview_viewed',
  tournaments: 'console.room_tournaments_viewed',
  leaderboards: 'console.room_leaderboards_viewed',
  fantasy: 'console.room_fantasy_viewed',
  ratings: 'console.room_ratings_viewed',
  players: 'console.room_players_viewed',
  templates: 'console.room_templates_viewed',
  points: 'console.room_points_systems_viewed',
  salary: 'console.room_salary_models_viewed',
  room: 'console.room_room_viewed',
  preferences: 'console.room_preferences_viewed',
};

/** Every room-web view or pane that reports nothing, and why (ADR 0012 step 29). */
export const UNTRACKED_VIEWS: Record<string, string> = {
  settings: 'A hub that opens on one of its panes, and the panes report themselves.',
};

/** What `App.tsx` renders, each declared (ADR 0012 step 29, `screens.coverage.test.js`). */
export const APP_ELEMENTS: Record<
  string,
  { kind: 'view' } | { kind: 'screen'; screen: RoomScreenName } | { kind: 'untracked'; reason: string }
> = {
  RoomShell: { kind: 'view' },
  Overview: { kind: 'view' },
  TournamentsView: { kind: 'view' },
  LeaderboardsView: { kind: 'view' },
  RatingsView: { kind: 'view' },
  ContestsView: { kind: 'view' },
  PlayersView: { kind: 'view' },
  SettingsView: { kind: 'view' },
  RoleChooser: {
    kind: 'untracked',
    reason: 'Asked before a role is in force; room-web reports a view at a room, and none is open yet.',
  },
  AccessDenied: {
    kind: 'untracked',
    reason: 'This sign-in holds no role here, or operates no room yet, so there is no room to report a view at.',
  },
  Spinner: { kind: 'untracked', reason: 'Loading: nothing has been read yet, so there is no screen to have viewed.' },
  ErrorRetry: {
    kind: 'untracked',
    reason: 'The API could not be reached, and a view report would go to the same API.',
  },
  Fragment: {
    kind: 'untracked',
    reason: 'Not a screen: the room key around the sections (#591), which report themselves inside it.',
  },
};
